import { useCallback, useEffect, useRef, useState } from "react";
import {
  useFollowerState,
  useLiveElapsed,
  useShortcutBindings,
} from "@cuekards/ui";
import {
  formatElapsed,
  isRecordingActive,
  type RecordingStatus,
} from "@cuekards/core/recording";
import { formatChord, type ShortcutCommand } from "@cuekards/core/shortcuts";
import {
  menuRows,
  pauseButton,
  recordButton,
  tooltip,
  type MenuCommand,
  type MenuRow,
} from "./control-bar.js";
import { DeviceCircle, type DeviceToggle } from "./device-circles.js";
import {
  FullScreenIcon,
  MoreIcon,
  PauseIcon,
  RecordDotIcon,
  ResumeIcon,
  StopSquareIcon,
} from "./icons.js";
import type { RefreshAvailability } from "@cuekards/core/refresh";

/**
 * The floating control bar (web-ui ticket 03), bottom centre.
 *
 * **The prompter's, and since flow ticket 04 the prompter's alone.** It was
 * mounted identically on the green room too, and the argument was that one bar in
 * one position is what makes two screens that are otherwise nothing like each
 * other feel like one app. That argument lost to a different one: the room is
 * about the script, and a floating transport in front of a screen you are typing
 * into is furniture from the wrong screen. What the two share now is the two
 * device circles, which is `device-circles.tsx`.
 *
 * It replaces `PrompterChrome`, a fixed top-left column of 12px pills sitting at
 * `opacity: 0.25` until hovered. That set did not go away — most of it is behind
 * the `⋯`, and the four things you reach for with a camera pointed at you came to
 * the front.
 *
 * **Everything it draws arrives through props or through the contract**, like the
 * rest of `page/`. The decisions it renders — which look the record button is in,
 * which rows the menu has, what is dimmed and why — are `control-bar.ts`, tested
 * at that seam, because a bar with no words on it is a bar whose labels can be
 * wrong without anything looking wrong.
 *
 * Two things in here are easy to get wrong and are written down where they are
 * done rather than here: the tooltips read the live binding ({@link chordFor}),
 * and the idle fade is the host's state and not the bar's — `useIdleFade` moved
 * to `./idle-fade.ts` in ticket 08 so the prompter's new top bar could recede on
 * the same timer rather than on a second one that agrees with it almost always.
 */

export function ControlBar({
  recording,
  loaded,
  anyDevice,
  camera,
  microphone,
  micOff,
  anchor,
  availability,
  idle = false,
  messages,
  onEdit,
  onSettings,
  onRefresh,
}: {
  recording: RecordingStatus;
  /** Whether there is a script to record against. */
  loaded: boolean;
  /**
   * Whether a take would open any device at all (web-ui ticket 06).
   *
   * Passed rather than read off the two toggles beside it, because the toggles
   * cannot answer it: a machine with no camera hands over no camera toggle, which
   * is indistinguishable here from a camera nobody has switched off. What the
   * host has and this does not is the constraints a take would actually open.
   *
   * Required rather than defaulted, unlike `RecordInputs.anyDevice` behind it: a
   * host that forgot to wire this would get a live record button over a take that
   * cannot open a device, and "forgot to wire it" is the failure a default would
   * be spelling as "everything is on".
   */
  anyDevice: boolean;
  camera?: DeviceToggle;
  microphone?: DeviceToggle;
  /**
   * Whether the user has switched the microphone off (web-ui ticket 06).
   *
   * Passed rather than read off `microphone` beside it, for the reason
   * {@link anyDevice} is passed: the toggle cannot answer it. `roomToggles` hands
   * over a circle that is `on: false` on a *blocked* camera too, because there is
   * no permission — and "you have not granted the microphone" is not "you
   * switched it off". Only the second is a thing Rehearse has something to say
   * about, and only the host holds it.
   */
  micOff: boolean;
  /**
   * Where the reading line currently sits, for the `Text align` row's label
   * (text-align ticket 01).
   *
   * From the **view state**, not the settings defaults — one value, one channel.
   * The backdrop beside it is read off `getOverlaySettings` because it never
   * reaches the prompter's own view; this does, so reading the defaults for it
   * would give one number two routes into the same screen, which is how a label
   * ends up naming a stop the words are not on.
   */
  anchor: number;
  /** Refresh's own rule (FR-17), decided by the host that knows whether one is
   * already in flight. */
  availability: RefreshAvailability;
  /**
   * Whether the screen has been left alone long enough for its chrome to recede.
   *
   * A *state* handed down rather than a policy the component applies to itself,
   * which is the change ticket 08 made to it. The prompter's top bar has to fade
   * with the bar, so the host owns one `useIdleFade` and gives the answer to
   * both; the green room passes nothing, because a room you are standing in does
   * not dim its own controls.
   *
   * The floor the record button holds is not decided here. It never goes below
   * 0.6 — a transport you have to hunt for is not a transport — and that is a CSS
   * rule rather than a second state, because two rules writing the same opacity
   * is how a control ends up brighter faded than lit.
   */
  idle?: boolean;
  /**
   * What has to be said above the bar, if anything — a refresh that failed, where
   * the last take went, what the follower is doing.
   *
   * Rendered inside the bar's dock rather than beside it, so one position and one
   * fade rule cover both. It is also the slot ticket 06's camera-off and mic-off
   * strips are specified to appear in.
   */
  messages?: React.ReactNode;
  onEdit: () => void;
  onSettings: () => void;
  onRefresh: () => void;
}): JSX.Element {
  const { phase } = recording;
  const bindings = useShortcutBindings("web");
  const [menuOpen, setMenuOpen] = useState(false);
  // An open menu holds the bar at full strength: the fade exists so the script
  // can be read past the chrome, and a panel you have just opened is the chrome
  // you are looking at.
  const faded = idle && !menuOpen;

  const record = recordButton({ phase, loaded, anyDevice });
  const pause = pauseButton(phase);
  const chordFor = (command: ShortcutCommand): string =>
    formatChord(bindings[command]);

  return (
    <div className={`web-bar-dock${faded ? " is-faded" : ""}`}>
      {messages}
      <div className="web-bar" role="group" aria-label="Recording controls">
        <DeviceCircle
          kind="microphone"
          className="web-bar-mic"
          device={microphone}
        />
        <DeviceCircle kind="camera" className="web-bar-camera" device={camera} />

        <span className="web-bar-divider" />

        {/* The pause slides in to the left of the record button when a take
          * starts, and the elapsed pill to its right — so the button itself never
          * moves, which is the point of putting them on either side of it. */}
        {pause !== null && (
          <button
            type="button"
            className="web-bar-pause"
            aria-label={pause.label}
            title={tooltip(pause.label, chordFor("pauseResume"))}
            onClick={() => window.cuekards.togglePauseRecording()}
            disabled={pause.disabled}
          >
            {pause.icon === "pause" ? <PauseIcon /> : <ResumeIcon />}
          </button>
        )}

        <button
          type="button"
          className={`web-bar-record look-${record.look}`}
          aria-label={record.label}
          title={
            record.reason ??
            tooltip(record.label, chordFor(record.action === "start" ? "record" : "stop"))
          }
          onClick={() =>
            record.action === "start"
              ? window.cuekards.startRecording()
              : window.cuekards.stopRecording()
          }
          disabled={record.disabled}
        >
          {record.icon === "dot" ? <RecordDotIcon /> : <StopSquareIcon />}
        </button>

        <Elapsed status={recording} />

        <span className="web-bar-divider" />

        <button
          type="button"
          className="web-bar-fullscreen"
          aria-label="Full screen"
          title={tooltip("Full screen")}
          onClick={() => void toggleFullScreen()}
        >
          <FullScreenIcon />
        </button>

        <MoreMenu
          open={menuOpen}
          onOpen={setMenuOpen}
          phase={phase}
          availability={availability}
          micOff={micOff}
          anchor={anchor}
          bindings={chordFor}
          onEdit={onEdit}
          onSettings={onSettings}
          onRefresh={onRefresh}
        />
      </div>
    </div>
  );
}

/**
 * How long the take has been running, in tabular figures.
 *
 * It duplicates the prompter badge's timer on purpose: the badge is what you see
 * in peripheral vision while reading, and this is what you reach for. Both count
 * from {@link useLiveElapsed}, which is why they cannot end up a second apart.
 */
function Elapsed({ status }: { status: RecordingStatus }): JSX.Element | null {
  const elapsed = useLiveElapsed(status);
  if (!isRecordingActive(status.phase)) return null;
  return (
    <span className={`web-bar-elapsed phase-${status.phase}`} role="status">
      <span className="rec-dot" />
      {formatElapsed(elapsed)}
    </span>
  );
}

/**
 * The `⋯`, and the panel that opens upward from it.
 *
 * Everything the old chrome carried that is not one of the four controls on the
 * bar, plus the keyboard commands that never had a button anywhere. The chord on
 * the right of each row is looked up when the panel is drawn rather than written
 * into the list, so rebinding a key changes what the menu says.
 *
 * **Escape closes it and puts focus back on the button that opened it.** A menu
 * you can open from the keyboard and only close with the mouse is a trap, and this
 * one can appear over a running take.
 */
function MoreMenu({
  open,
  onOpen,
  phase,
  availability,
  micOff,
  anchor,
  bindings,
  onEdit,
  onSettings,
  onRefresh,
}: {
  open: boolean;
  onOpen: (open: boolean) => void;
  phase: RecordingStatus["phase"];
  availability: RefreshAvailability;
  /**
   * Whether the user has switched the microphone off (web-ui ticket 06).
   *
   * Rehearse's own rule needs it, and it is read off the same toggle the circle
   * beside this menu is drawn from, so the row and the button cannot disagree.
   * **Off, not absent**: a machine with no microphone hands over no toggle at
   * all, which is a different sentence and not one this row is in a position to
   * say.
   */
  micOff: boolean;
  /** Where the reading line sits, which is what the `Text align` row says. The
   * host's, off the prompter's own view state — see {@link ControlBar}. */
  anchor: number;
  bindings: (command: ShortcutCommand) => string;
  onEdit: () => void;
  onSettings: () => void;
  onRefresh: () => void;
}): JSX.Element {
  const wrapper = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  /* Read here rather than in {@link ControlBar}, so a screen with the panel shut
   * is not holding a subscription for the one row that depends on it — the menu
   * is unmounted while it is closed. */
  const follower = useFollowerState();

  const close = useCallback(
    (returnFocus: boolean) => {
      onOpen(false);
      if (returnFocus) button.current?.focus();
    },
    [onOpen],
  );

  // Pressing anywhere else puts it away, the way every menu does. `pointerdown`
  // rather than `click`, so it closes on the way down and the press that closed
  // it does not also land on whatever was underneath.
  useEffect(() => {
    if (!open) return;
    const onDown = (event: PointerEvent): void => {
      if (!wrapper.current?.contains(event.target as Node)) close(false);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [open, close]);

  /**
   * What each row does. Written whole, so a command added to {@link MenuCommand}
   * and not to this is a compile error rather than a row that does nothing —
   * which is the rule `useShortcuts` runs its own table on, for the same reason.
   */
  const run: Record<MenuCommand, () => void> = {
    rehearse: () => window.cuekards.toggleRehearsal(),
    restart: () => window.cuekards.restartPosition(),
    nudgeBack: () => window.cuekards.nudgePosition(-1),
    nudgeForward: () => window.cuekards.nudgePosition(1),
    resync: () => window.cuekards.resyncFollower(),
    cycleMode: () => window.cuekards.cycleScrollMode(),
    fontDown: () => window.cuekards.stepFontSize(-1),
    fontUp: () => window.cuekards.stepFontSize(1),
    textAlign: () => window.cuekards.cycleTextAlign(),
    refresh: onRefresh,
    edit: onEdit,
    settings: onSettings,
  };

  /** Which rows put the menu away when they are pressed: the ones that change the
   * screen. The position and display commands stay open, so a font size can be
   * stepped twice without reopening the panel — and `textAlign` is in that half
   * for a sharper version of the same reason: it is three stops on a cycle, and
   * finding the right one means pressing it two or three times while watching the
   * words move. `help` was a fourth until flow ticket 07 — it opened a tab, which
   * is a change of screen too. */
  const closesOnPress = new Set<MenuCommand>(["edit", "settings", "refresh"]);

  return (
    <div
      className="web-bar-more"
      ref={wrapper}
      onKeyDown={(event) => {
        if (event.key !== "Escape") return;
        event.stopPropagation();
        close(true);
      }}
    >
      <button
        type="button"
        className="web-bar-menu-button"
        ref={button}
        aria-label="More"
        title={tooltip("More")}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => onOpen(!open)}
      >
        <MoreIcon />
      </button>
      {open && (
        <div className="web-bar-menu" role="group" aria-label="More controls">
          {menuRows({
            phase,
            follower,
            availability,
            micOff,
            anchor,
          }).map((row) => (
            <Row
              key={row.command}
              row={row}
              chord={row.chord === undefined ? undefined : bindings(row.chord)}
              onPress={() => {
                run[row.command]();
                if (closesOnPress.has(row.command)) close(false);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** One menu row: what it does on the left, the key it is on down the right. The
 * `data-command` is the handle the browser drivers press it by. */
function Row({
  row,
  chord,
  onPress,
}: {
  row: MenuRow;
  chord?: string;
  onPress: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      data-command={row.command}
      disabled={row.disabled}
      {...(row.reason === undefined ? {} : { title: row.reason })}
      onClick={onPress}
    >
      <span>{row.label}</span>
      {chord !== undefined && <span className="web-bar-menu-key">{chord}</span>}
    </button>
  );
}

/**
 * Fill the monitor beside the lens, which is what a prompter is for.
 *
 * Guarded rather than assumed: full screen is only granted from a user gesture,
 * and a browser is entitled to refuse. A refusal leaves a prompter filling the tab
 * instead, which is still a prompter.
 */
async function toggleFullScreen(): Promise<void> {
  try {
    if (document.fullscreenElement) await document.exitFullscreen();
    else await document.documentElement.requestFullscreen();
  } catch {
    // Refused. The page is unchanged and still usable.
  }
}
